export default function EdmondTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel & Passport Timing for Edmond Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Edmond travelers actually move helps us anticipate the passport urgencies that arise here. From UCO study abroad cycles to family vacation planning, north OKC metro travel operates on a different rhythm than most U.S. markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* OKC Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">OKC — Will Rogers World Airport</h3>
                <p className="text-gray-400 text-xs">Southwest Oklahoma City, 15 miles from Edmond</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Will Rogers World Airport (OKC) is the primary commercial airport for Edmond and north OKC metro residents. The airport offers nonstop service to major domestic hubs including Dallas, Denver, Chicago, Atlanta, and Houston — with international connections throughout. Edmond professionals frequently use OKC for last-minute bookings to Dallas and onward to international destinations. The airport's compact size means check-in can be quick, but that does not help if your passport is expired.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Many Edmond professionals book international travel through Dallas or Houston hubs. Even if your first leg is domestic, international connections require a valid passport — check yours before booking.
              </p>
            </div>
          </div>

          {/* UCO Study Abroad */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">UCO Study Abroad & International Programs</h3>
                <p className="text-gray-400 text-xs">University of Central Oklahoma, 100 N University Dr</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The University of Central Oklahoma sends hundreds of students abroad each academic year through its Global Learning program and international education partnerships. Business and education students participate in exchange programs to Germany, Spain, and Southeast Asia. Study abroad application deadlines typically fall in October and February, but many students do not think about their passport until after acceptance. We guide UCO students through the acceptance clerk process with a personalized checklist so they do not miss their orientation deadlines.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (many UCO students) require an in-person acceptance clerk visit. We guide Edmond students through the exact location, forms, and timeline so they do not miss their program orientation.
              </p>
            </div>
          </div>

          {/* Family Vacation Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-heart-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Family Vacation & River Cruise Planning</h3>
                <p className="text-gray-400 text-xs">Oak Tree, Coffee Creek, Spring Hollow & Deer Creek</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Edmond's affluent neighborhoods generate significant family passport demand, particularly in spring and summer. European river cruises, Caribbean all-inclusive resorts, and Mexican beach vacations are the most common destinations. Many Edmond families book their vacations 6–12 months in advance but do not check passport expiration until the final payment is due. With multiple family members needing renewals simultaneously, group coordination and child passport requirements can create complex timelines. We help Edmond families coordinate multiple applications to ensure everyone is ready for departure.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Edmond families: check every family member's passport expiration when you book your vacation — not when final payment is due. Child passports require both parents to appear together or submit a notarized consent form.
              </p>
            </div>
          </div>

          {/* Business Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-briefcase-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Edmond Business & Professional Travel</h3>
                <p className="text-gray-400 text-xs">INTEGRIS, tech corridor, and downtown OKC commuters</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Edmond's professional community — physicians at INTEGRIS, attorneys, tech entrepreneurs, and executives commuting to downtown OKC — generates consistent business travel demand. Conference travel to London, Frankfurt, and Tokyo; medical conference attendance in Europe; and client meetings in Canada and Mexico are common. Many professionals discovered their passports expired during the COVID-era travel lull. The combination of high travel frequency and compressed booking timelines makes Edmond professionals one of our most time-sensitive client categories.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Professionals who travel internationally more than twice a year should consider a second passport to allow continuous travel while one passport is being processed. We handle second passport applications too.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Edmond Conference Center & Corporate Event Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Edmond's conference venues and business centers host corporate events that draw regional and international business travel. Event organizers, keynote speakers, and corporate attendees flying in from abroad — or Edmond executives traveling to partner events overseas — sometimes discover passport issues during event planning. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}